import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  KanbanSquare,
  ChevronLeft,
  ChevronRight,
  Wallet,
  ClipboardList,
  Warehouse,
  UserCheck,
  BarChart3,
  ShoppingCart,
  Megaphone,
  FolderArchive,
  ShieldCheck,
  Lock,
  Bell,
  Video,
  Briefcase,
  TrendingUp,
  FileText,
  GraduationCap,
  X,
  Mail,
  Package,
  Smartphone,
  Wrench,
  MessagesSquare,
} from 'lucide-react';
import { useDiscussions } from '../../context/DiscussionsContext';
import AgendaWidget from './AgendaWidget';
import usePwaInstall from '../../hooks/usePwaInstall';

// Mapping clé d'onglet (côté backend) → entrée sidebar.
// La visibilité est désormais pilotée par /api/access/my-tabs (per-user),
// la propriété 'roles' reste pour fallback / rétro-compat hors-ligne.
const NAV_ITEMS = [
  { key: 'dashboard', to: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
  { key: 'discussions', to: '/discussions', icon: MessagesSquare, label: 'Discussions', toujoursVisible: true },
  { key: 'contacts', to: '/contacts', icon: Users, label: 'CRM / Contacts' },
  { key: 'relances', to: '/relances', icon: Bell, label: 'Relances clients' },
  { key: 'pipeline', to: '/pipeline', icon: KanbanSquare, label: 'Pipeline ventes' },
  // Chacun y voit sa propre carte, l'admin toute l'equipe (filtre serveur).
  { key: 'commercial', to: '/kpi', icon: TrendingUp, label: 'KPI', toujoursVisible: true },
  { key: 'formation', to: '/formation', icon: GraduationCap, label: 'Espace Formation' },
  { key: 'content', to: '/content', icon: Video, label: 'Contenu & Réseaux' },
  { key: 'rh', to: '/rh', icon: UserCheck, label: 'RH & Équipe' },
  { key: 'management', to: '/management', icon: Users, label: 'Management' },
  { key: 'stock', to: '/stock', icon: Warehouse, label: 'Stock & Inventaire' },
  { key: 'reports', to: '/reports', icon: FileText, label: 'Rapport mensuel' },
  { key: 'projects', to: '/projects', icon: ClipboardList, label: 'Projets & Tâches' },
  { key: 'documents', to: '/documents', icon: FolderArchive, label: 'Documents' },
  { key: 'finance', to: '/finance', icon: Wallet, label: 'Finance & Compta' },
  { key: 'depots-vente', to: '/depots-vente', icon: Package, label: 'Dépôts-vente' },
  { key: 'sav', to: '/sav', icon: Wrench, label: 'SAV / Interventions' },
  { key: 'rechercher-devis', to: '/rechercher-devis', icon: FileText, label: 'Recherche rapide' },
  { key: 'stats-vendeurs', to: '/stats-vendeurs', icon: BarChart3, label: 'Stats vendeurs caisse' },
  { key: 'admin-caisse', to: '/admin-caisse', icon: ShoppingCart, label: 'Admin Caisse' },
  { key: 'audit', to: '/audit', icon: ShieldCheck, label: 'Logs & Accès' },
  { key: 'admin-access', to: '/admin/access', icon: Lock, label: 'Gestion des accès', adminOnly: true },
  // Pas adminOnly : l'onglet s'accorde dans Gestion des acces. L'admin le voit
  // toujours, meme sans droit explicite, sinon il disparaitrait de son menu.
  { key: 'email-senders', to: '/admin/email-senders', icon: Mail, label: 'Comptes email', adminOuAccorde: true },
  { key: 'doc-settings', to: '/admin/documents', icon: FileText, label: 'Documents & logo', adminOnly: true },
];

const UPCOMING_ITEMS = [
  { icon: Megaphone, label: 'Marketing' },
];

export default function Sidebar({ collapsed, onToggle, onClose, isMobile, onOpenPlanning }) {
  const { user, hasRole } = useAuth();
  const { canInstall, install } = usePwaInstall();
  const [allowedTabs, setAllowedTabs] = React.useState(null);

  // Charge la liste des onglets autorisés pour cet utilisateur
  // (combinaison de ses rôles + des grants explicites par l'admin)
  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    import('../../api/axios').then(({ default: api }) => {
      api.get('/access/my-tabs')
        .then(r => { if (!cancelled) setAllowedTabs(new Set(r.data?.tabs || [])); })
        .catch(() => { if (!cancelled) setAllowedTabs(null); /* fallback rôles */ });
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  // On mobile, force icons-only (condensed) drawer
  const isCollapsed = isMobile ? true : collapsed;

  const roleLabels = {
    admin: 'Administrateur',
    manager: 'Manager',
    closeur: 'Closeur',
    commercial: 'Commercial',
    formateur: 'Formateur',
    responsable_commercial: 'Resp. Commercial',
    responsable_formation: 'Resp. Formation',
    responsable_rh: 'Resp. RH',
    responsable_stocks: 'Resp. Stocks',
  };

  return (
    <aside className={`bg-slate-800 border-r border-slate-700 h-screen flex flex-col ${isCollapsed ? 'w-16' : 'w-64'} transition-all duration-300`}>
      {/* Header */}
      <div className={`${isMobile ? 'px-2 py-3' : 'p-4'} border-b border-slate-700 flex items-center ${isMobile ? 'justify-center' : 'justify-between'}`}>
        {!isMobile && (
          isCollapsed ? (
            <img src="/logo.png" alt="Datashipset" className="w-10 h-10 object-contain" />
          ) : (
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="Datashipset" className="w-10 h-10 object-contain" />
              <h1 className="text-lg font-bold text-white">Datashipset</h1>
            </div>
          )
        )}
        {/* Mobile: close button, Desktop: collapse toggle */}
        {isMobile ? (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        ) : (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white hidden lg:block"
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        )}
      </div>

      {/* Navigation — deux tiers de la hauteur, l'agenda occupe le tiers bas */}
      <nav className="flex-[2] p-3 space-y-1 overflow-y-auto min-h-0">
        {NAV_ITEMS.filter(item => {
          if (item.toujoursVisible) return true; // chat d'equipe : pour tout le monde
          // Onglets admin-only : check pur sur le rôle (sinon l'admin pourrait
          // se verrouiller hors de la page qui gère les accès).
          if (item.adminOnly) return hasRole('admin');
          // Accessible a l'admin d'office, et a qui l'onglet a ete accorde.
          if (item.adminOuAccorde) return hasRole('admin') || (allowedTabs ? allowedTabs.has(item.key) : false);
          // Pour tout le monde (admin inclus) : strictement les onglets cochés.
          if (allowedTabs) return allowedTabs.has(item.key);
          return true; // évite de cacher pendant le chargement initial
        }).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={onClose}
            title={item.label}
            className={({ isActive }) =>
              `flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-purple-600/20 text-purple-400'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`
            }
          >
            <item.icon size={20} />
            {!isCollapsed && <span>{item.label}</span>}
            {item.key === 'discussions' && !isCollapsed && <BadgeDiscussions />}
          </NavLink>
        ))}

        {/* Modules à venir — hidden on mobile for cleaner nav */}
        {!isCollapsed && !isMobile && (
          <>
            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Bientôt disponible</p>
            </div>
            {UPCOMING_ITEMS.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 cursor-not-allowed select-none"
                title={`${item.label} — Bientôt disponible`}
              >
                <item.icon size={20} className="opacity-40" />
                <span className="opacity-40 flex-1">{item.label}</span>
                <Lock size={12} className="opacity-30" />
              </div>
            ))}
          </>
        )}
      </nav>

      {/* Agenda — tiers bas. Masqué en mode replié (pas la place) et sur le
          drawer mobile, où l'agenda a déjà son onglet dans la barre du bas. */}
      {!isCollapsed && !isMobile && (
        <div className="flex-[1] min-h-0 p-3 border-t border-slate-700 bg-slate-900/40">
          <AgendaWidget onOpenPlanning={onOpenPlanning} />
        </div>
      )}

      {/* Pied — Caisse, notifications, utilisateur et déconnexion vivent
          désormais dans la barre du haut (TopBar). Ne reste ici que
          l'installation PWA, qui n'a pas sa place dans un en-tête et
          n'apparaît que si le navigateur la propose. */}
      {canInstall && (
        <div className="p-3 border-t border-slate-700 shrink-0">
          <button onClick={install}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 w-full`}
            title="Installer Datashipset comme une application">
            <Smartphone size={20} />
            {!isCollapsed && <span>Installer l'app</span>}
          </button>
        </div>
      )}
    </aside>
  );
}

// Badge non-lus de l'entree Discussions (sous DiscussionsProvider via Layout).
function BadgeDiscussions() {
  const { unreadTotal } = useDiscussions();
  if (!unreadTotal) return null;
  return (
    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-purple-500 text-white text-[11px] flex items-center justify-center">
      {unreadTotal > 99 ? '99+' : unreadTotal}
    </span>
  );
}
